/* ============================================================================
 * CrewRemindersCard — crew portal (code, no login). Migration 115.
 *
 * Lets a crew member turn on the daily reminders for themselves: phone
 * notifications on this device, and/or texts to their own number with the
 * consent wording shown (and stored server-side) before they agree.
 * ========================================================================== */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { BellRing, Loader2, MessageSquareText } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  currentPushSubscription,
  disablePushOnThisDevice,
  enablePushOnThisDevice,
  fetchCrewReminderStatus,
  pushSupport,
  rememberCrewHome,
  saveCrewDevice,
  setCrewSms,
} from '@/lib/reminders';

export default function CrewRemindersCard({ code }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [deviceOn, setDeviceOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showSms, setShowSms] = useState(false);
  const [phone, setPhone] = useState('');
  const [agree, setAgree] = useState(false);
  const support = pushSupport();

  const load = async () => {
    try {
      const s = await fetchCrewReminderStatus(code);
      if (s?.ok) setStatus(s);
    } catch (_) {
      /* the card hides itself */
    }
  };

  useEffect(() => {
    if (!code) return;
    load();
    currentPushSubscription().then((sub) => setDeviceOn(!!sub));
    // An iPhone added to the Home Screen from here should open back here.
    if (support === 'ios_install') rememberCrewHome(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  if (!status) return null;

  const turnOn = async () => {
    setBusy(true);
    try {
      const r = await enablePushOnThisDevice(status.push_key, saveCrewDevice(code));
      if (r.ok) {
        setDeviceOn(true);
        rememberCrewHome(code);
        toast.success(t('dailyReminders.crew_device_on_toast'));
      } else {
        toast.error(t(`dailyReminders.push_err_${r.error}`, { defaultValue: t('dailyReminders.push_err_generic') }));
      }
    } catch (_) {
      toast.error(t('dailyReminders.push_err_generic'));
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    setBusy(true);
    try {
      await disablePushOnThisDevice();
      setDeviceOn(false);
    } finally {
      setBusy(false);
    }
  };

  const sms = async (on) => {
    setBusy(true);
    try {
      const r = await setCrewSms(code, phone, on);
      if (!r?.ok) {
        toast.error(t(`dailyReminders.sms_err_${r?.error}`, { defaultValue: t('dailyReminders.sms_err_generic') }));
        return;
      }
      toast.success(on ? t('dailyReminders.sms_on_toast') : t('dailyReminders.sms_off_toast'));
      setPhone('');
      setAgree(false);
      setShowSms(false);
      await load();
    } catch (_) {
      toast.error(t('dailyReminders.sms_err_generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mt-4 border-[hsl(var(--hairline))] bg-[hsl(var(--card))]" data-testid="crew-reminders-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-2">
          <BellRing size={16} className="mt-0.5 shrink-0 text-[hsl(var(--primary))]" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground">{t('dailyReminders.crew_card_title')}</div>
            <div className="text-xs text-muted-foreground">{t('dailyReminders.crew_card_help')}</div>
          </div>
        </div>

        {support === 'ios_install' ? (
          <p className="text-xs text-muted-foreground" data-testid="crew-reminders-ios">
            {t('dailyReminders.push_err_ios_install')}
          </p>
        ) : support === 'ok' ? (
          deviceOn ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">{t('dailyReminders.device_on')}</span>
              <Button type="button" variant="outline" size="sm" onClick={turnOff} disabled={busy} className="h-9 border-border">
                {t('dailyReminders.device_turn_off')}
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              onClick={turnOn}
              disabled={busy || !status.push_key}
              className="h-11 w-full bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="crew-reminders-push-on"
            >
              {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <BellRing size={14} className="mr-2" />}
              {t('dailyReminders.device_turn_on')}
            </Button>
          )
        ) : null}

        {status.sms_last4 ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-foreground">{t('dailyReminders.sms_on', { last4: status.sms_last4 })}</span>
            <Button type="button" variant="outline" size="sm" onClick={() => sms(false)} disabled={busy} className="h-9 border-border">
              {t('dailyReminders.sms_turn_off')}
            </Button>
          </div>
        ) : showSms ? (
          <div className="space-y-2" data-testid="crew-reminders-sms-form">
            <Input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder={t('dailyReminders.phone_placeholder')}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="h-11 bg-[hsl(var(--muted))] border-border text-foreground"
            />
            <label className="flex items-start gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0"
              />
              <span>{status.consent_text}</span>
            </label>
            <Button
              type="button"
              variant="outline"
              onClick={() => sms(true)}
              disabled={busy || !agree || !phone.trim()}
              className="h-11 w-full border-border"
              data-testid="crew-reminders-sms-on"
            >
              {t('dailyReminders.sms_turn_on')}
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowSms(true)}
            className="flex items-center gap-1.5 text-xs text-[hsl(var(--primary))] underline underline-offset-2"
            data-testid="crew-reminders-sms-open"
          >
            <MessageSquareText size={12} />
            {support === 'ok' ? t('dailyReminders.crew_text_instead') : t('dailyReminders.crew_text_me')}
          </button>
        )}
      </CardContent>
    </Card>
  );
}
